import { useState, useEffect } from 'react';
import api from '../../services/api';

// Interface para prontuários da lista (dadosAgregados - snake_case)
interface ProntuarioLista {
  consulta_id: number;
  data_consulta: string;
}

// Interface para prontuário detalhado da API (camelCase com arrays)
interface ProntuarioDetalhado {
  consultaId: number;
  historicoSessao?: string;
  queixas?: Array<{
    id: number;
    valor: string;
    observacao?: string;
    intensidade?: number;
  }>;
  sintomas?: Array<{
    id: number;
    valor: string;
    observacao?: string;
    intensidade?: number;
  }>;
  humor?: Array<{
    id: number;
    valor: string;
    observacao?: string;
    predominante?: boolean;
  }>;
  medicamentos?: Array<{
    id: number;
    nome: string;
    dosagem?: string;
    frequencia?: string;
  }>;
  evolucao?: string;
  hipoteseDiagnostica?: string;
  planoTratamento?: string;
  encaminhamentos?: string;
  observacoes?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  pacienteNome: string;
  prontuarios: ProntuarioLista[];
}

export default function ComparadorProntuarios({ 
  show, 
  onClose, 
  pacienteNome,
  prontuarios 
}: Props) {
  const [prontuario1, setProntuario1] = useState<ProntuarioDetalhado | null>(null);
  const [prontuario2, setProntuario2] = useState<ProntuarioDetalhado | null>(null);
  const [loadingProntuario1, setLoadingProntuario1] = useState(false);
  const [loadingProntuario2, setLoadingProntuario2] = useState(false);

  // ========================================
  // Carregar último prontuário ao abrir
  // ========================================
  useEffect(() => {
    if (show && prontuarios.length > 0 && !prontuario1) {
      const ultimoProntuario = prontuarios[0]; // Já vem ordenado DESC
      carregarProntuario(ultimoProntuario.consulta_id, 1);
    }
  }, [show, prontuarios]);

  // ========================================
  // CARREGAR PRONTUÁRIO COMPLETO
  // ========================================
  const carregarProntuario = async (consultaId: number, slot: 1 | 2) => {
    try {
      const setLoading = slot === 1 ? setLoadingProntuario1 : setLoadingProntuario2;
      const setProntuario = slot === 1 ? setProntuario1 : setProntuario2;

      setLoading(true);

      console.log(`[COMPARADOR] Buscando prontuário da consulta ${consultaId}...`);

      const response = await api.get(`/prontuarios/consulta/${consultaId}`);

      if (response.data) {
        setProntuario(response.data);
        console.log(`[COMPARADOR] Prontuário carregado com sucesso (slot ${slot})`);
      } else {
        throw new Error('Prontuário não encontrado');
      }
    } catch (error: any) {
      console.error('[COMPARADOR] Erro ao carregar prontuário:', error);
      alert('Erro ao carregar prontuário: ' + (error.response?.data?.mensagem || error.message));
    } finally {
      const setLoading = slot === 1 ? setLoadingProntuario1 : setLoadingProntuario2;
      setLoading(false);
    }
  };

  // ========================================
  // FORMATAR DATA
  // ========================================
  const formatarData = (data: string) => {
    if (!data) return 'Data não disponível';
    try {
      // Garantir que a data está no formato correto
      const dataObj = new Date(data);
      if (isNaN(dataObj.getTime())) return 'Data inválida';
      
      return dataObj.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('[COMPARADOR] Erro ao formatar data:', error);
      return 'Data inválida';
    }
  };

  // ========================================
  // FECHAR E LIMPAR
  // ========================================
  const handleFechar = () => {
    setProntuario1(null);
    setProntuario2(null);
    onClose();
  };

  if (!show) return null;

  return (
    <div 
      className="modal fade show d-block" 
      tabIndex={-1} 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
    >
      <div className="modal-dialog modal-xl" style={{ maxWidth: '95%' }}>
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="fas fa-file-medical me-2"></i>
              Comparador de Prontuários - {pacienteNome}
            </h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={handleFechar}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="row">
              {/* Coluna 1: Prontuário Esquerdo */}
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header bg-info text-white">
                    <h6 className="mb-0">
                      <i className="fas fa-file-alt me-2"></i>
                      Prontuário 1
                    </h6>
                  </div>
                  <div className="card-body">
                    {/* Seletor de Prontuário */}
                    <div className="mb-3">
                      <label className="form-label fw-bold" htmlFor="comparadorProntuario1">Selecionar Prontuário:</label>
                      <select
                        id="comparadorProntuario1"
                        className="form-select"
                        value={prontuario1?.consultaId || ''}
                        onChange={(e) => {
                          const consultaId = parseInt(e.target.value);
                          if (consultaId) carregarProntuario(consultaId, 1);
                        }}
                      >
                        <option value="">-- Selecione --</option>
                        {prontuarios
                          .sort((a, b) => new Date(a.data_consulta).getTime() - new Date(b.data_consulta).getTime())
                          .map((p, index) => (
                          <option key={p.consulta_id} value={p.consulta_id}>
                            Sessão #{index + 1} - {formatarData(p.data_consulta)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Conteúdo do Prontuário */}
                    {loadingProntuario1 ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Carregando...</span>
                        </div>
                      </div>
                    ) : prontuario1 ? (
                      <RenderProntuario prontuario={prontuario1} />
                    ) : (
                      <p className="text-muted text-center">Selecione um prontuário</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Coluna 2: Prontuário Direito */}
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header bg-success text-white">
                    <h6 className="mb-0">
                      <i className="fas fa-file-alt me-2"></i>
                      Prontuário 2
                    </h6>
                  </div>
                  <div className="card-body">
                    {/* Seletor de Prontuário */}
                    <div className="mb-3">
                      <label className="form-label fw-bold" htmlFor="comparadorProntuario2">Selecionar Prontuário:</label>
                      <select
                        id="comparadorProntuario2"
                        className="form-select"
                        value={prontuario2?.consultaId || ''}
                        onChange={(e) => {
                          const consultaId = parseInt(e.target.value);
                          if (consultaId) carregarProntuario(consultaId, 2);
                        }}
                      >
                        <option value="">-- Selecione --</option>
                        {prontuarios
                          .sort((a, b) => new Date(a.data_consulta).getTime() - new Date(b.data_consulta).getTime())
                          .map((p, index) => (
                          <option key={p.consulta_id} value={p.consulta_id}>
                            Sessão #{index + 1} - {formatarData(p.data_consulta)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Conteúdo do Prontuário */}
                    {loadingProntuario2 ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-success" role="status">
                          <span className="visually-hidden">Carregando...</span>
                        </div>
                      </div>
                    ) : prontuario2 ? (
                      <RenderProntuario prontuario={prontuario2} />
                    ) : (
                      <p className="text-muted text-center">Selecione um prontuário para comparar</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={handleFechar}
            >
              <i className="fas fa-times me-2"></i>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========================================
// COMPONENTE: Renderizar Prontuário
// ========================================
function RenderProntuario({ prontuario }: { prontuario: ProntuarioDetalhado }) {
  const formatarDataHora = (data: string) => {
    if (!data) return 'Data não disponível';
    try {
      const dataObj = new Date(data);
      if (isNaN(dataObj.getTime())) return 'Data inválida';
      
      return dataObj.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data inválida';
    }
  };

  return (
    <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
      {/* Info da Sessão */}
      <div className="alert alert-light border mb-3">
        <div className="row">
          <div className="col-4">
            <small className="text-muted d-block">Consulta ID</small>
            <strong>#{prontuario.consultaId}</strong>
          </div>
          <div className="col-4">
            <small className="text-muted d-block">Criado em</small>
            <strong>{prontuario.createdAt ? formatarDataHora(prontuario.createdAt) : 'N/A'}</strong>
          </div>
          <div className="col-4">
            <small className="text-muted d-block">Atualizado em</small>
            <strong>{prontuario.updatedAt ? formatarDataHora(prontuario.updatedAt) : 'N/A'}</strong>
          </div>
        </div>
      </div>

      {/* Histórico da Sessão */}
      {prontuario.historicoSessao && (
        <div className="mb-3">
          <h6 className="text-primary border-bottom pb-2">
            <i className="fas fa-history me-2"></i>
            Histórico da Sessão
          </h6>
          <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
            {prontuario.historicoSessao}
          </p>
        </div>
      )}

      {/* Queixas Principais */}
      {prontuario.queixas && prontuario.queixas.length > 0 && (
        <div className="mb-3">
          <h6 className="text-warning border-bottom pb-2">
            <i className="fas fa-comments me-2"></i>
            Queixas Principais
          </h6>
          <ul className="list-unstyled">
            {prontuario.queixas.map((queixa: any, idx: number) => (
              <li key={idx} className="mb-2">
                <strong>{queixa.valor}</strong>
                {queixa.intensidade && <span className="badge bg-warning ms-2">{queixa.intensidade}/10</span>}
                {queixa.observacao && (
                  <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                    {queixa.observacao}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Sintomas Relatados */}
      {prontuario.sintomas && prontuario.sintomas.length > 0 && (
        <div className="mb-3">
          <h6 className="text-danger border-bottom pb-2">
            <i className="fas fa-heartbeat me-2"></i>
            Sintomas Relatados
          </h6>
          <ul className="list-unstyled">
            {prontuario.sintomas.map((sintoma: any, idx: number) => (
              <li key={idx} className="mb-2">
                <strong>{sintoma.valor}</strong>
                {sintoma.intensidade && <span className="badge bg-danger ms-2">{sintoma.intensidade}/10</span>}
                {sintoma.observacao && (
                  <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                    {sintoma.observacao}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Humor */}
      {prontuario.humor && prontuario.humor.length > 0 && (
        <div className="mb-3">
          <h6 className="text-info border-bottom pb-2">
            <i className="fas fa-smile me-2"></i>
            Humor
          </h6>
          <ul className="list-unstyled">
            {prontuario.humor.map((h: any, idx: number) => (
              <li key={idx} className="mb-2">
                <strong>{h.valor}</strong>
                {h.predominante && <span className="badge bg-info ms-2">Predominante</span>}
                {h.observacao && (
                  <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                    {h.observacao}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Evolução */}
      {prontuario.evolucao && (
        <div className="mb-3">
          <h6 className="text-success border-bottom pb-2">
            <i className="fas fa-chart-line me-2"></i>
            Evolução
          </h6>
          <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
            {prontuario.evolucao}
          </p>
        </div>
      )}

      {/* Hipótese Diagnóstica */}
      {prontuario.hipoteseDiagnostica && (
        <div className="mb-3">
          <h6 className="text-dark border-bottom pb-2">
            <i className="fas fa-stethoscope me-2"></i>
            Hipótese Diagnóstica
          </h6>
          <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
            {prontuario.hipoteseDiagnostica}
          </p>
        </div>
      )}

      {/* Plano de Tratamento */}
      {prontuario.planoTratamento && (
        <div className="mb-3">
          <h6 className="text-primary border-bottom pb-2">
            <i className="fas fa-clipboard-list me-2"></i>
            Plano de Tratamento
          </h6>
          <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
            {prontuario.planoTratamento}
          </p>
        </div>
      )}

      {/* Encaminhamentos */}
      {prontuario.encaminhamentos && (
        <div className="mb-3">
          <h6 className="text-info border-bottom pb-2">
            <i className="fas fa-share me-2"></i>
            Encaminhamentos
          </h6>
          <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
            {prontuario.encaminhamentos}
          </p>
        </div>
      )}

      {/* Observações */}
      {prontuario.observacoes && (
        <div className="mb-3">
          <h6 className="text-secondary border-bottom pb-2">
            <i className="fas fa-sticky-note me-2"></i>
            Observações
          </h6>
          <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
            {prontuario.observacoes}
          </p>
        </div>
      )}
    </div>
  );
}
